'use client';

import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Image from "next/image";
import Link from "next/link";
import { useParams } from "next/navigation";
import React, { use } from "react";
import { localizeSpelling } from "@/lib/spelling-localization";

const caseStudies = [
  {
    id: 1,
    title: "AI-Powered Customer Service",
    client: "Global Tech Solutions",
    description: "Implemented an intelligent chatbot system that reduced response time by 80%",
    image: undefined,
    tags: ["NLP", "Machine Learning", "Customer Service"],
    slug: "ai-customer-service",
    gradient: "from-violet-600 via-indigo-500 to-blue-500"
  },
  {
    id: 2,
    title: "Computer Vision for Quality Control",
    client: "Manufacturing Excellence",
    description: "Automated quality inspection system that improved accuracy by 95%",
    image: undefined,
    tags: ["Computer Vision", "Quality Control", "Automation"],
    slug: "vision-quality-control",
    gradient: "from-cyan-500 via-blue-600 to-purple-600"
  },
  {
    id: 3,
    title: "Predictive Analytics Platform",
    client: "Data Insights Corp",
    description: "Built a predictive maintenance system that reduced downtime by 60%",
    image: undefined,
    tags: ["Analytics", "Machine Learning", "Predictive Maintenance"],
    slug: "predictive-analytics",
    gradient: "from-fuchsia-500 via-purple-600 to-indigo-500"
  }
];

const industryUseCases = [
  {
    industry: "AutoTech",
    color: "from-red-500 via-orange-400 to-amber-500",
    cases: [
      {
        title: "Compliance Scanner",
        slug: "compliance-scanner",
        challenge: "Ensuring vehicles meet diverse and evolving compliance standards efficiently, minimizing recall risks and accelerating market entry.",
        applications: [
          "AI-driven automated compliance checks for emissions, safety, and build quality",
          "Real-time monitoring and reporting of compliance status using sensor fusion",
          "Computer Vision for verifying component adherence to specifications"
        ],
        benefits: "Potential outcomes include streamlined regulatory adherence, 65-70% reduction in compliance-related delays, and enhanced safety."
      },
      {
        title: "Fleet Intelligence & Management Systems",
        slug: "fleet-management-dashboard",
        challenge: "Commercial fleet operators struggle with maintenance costs, vehicle downtime, fuel efficiency, and driver safety across large, distributed vehicle networks.",
        applications: [
          "IoT-enabled predictive maintenance with AI diagnostics to prevent breakdowns",
          "Route optimization using real-time traffic data and vehicle telemetry",
          "Driver behavior monitoring with computer vision and sensor fusion for safety scoring",
          "Integrated fuel optimization systems with automated reporting and analytics"
        ],
        benefits: "Implementation can deliver 40-45% decrease in maintenance expenses, 50-55% reduction in vehicle downtime, and 25-30% improvement in operational efficiency."
      },
      {
        title: "Risk Assessment & Specialized Coverage Models",
        slug: "risk-assessment-models",
        challenge: "Auto insurance providers need more accurate risk assessment tools, efficient claims processing, and data-driven models for usage-based insurance products.",
        applications: [
          "AI-driven risk models using vehicle telemetry and driving pattern analysis",
          "Computer vision systems for automated damage assessment and claims processing",
          "Real-time driving behavior monitoring for dynamic premium adjustments",
          "Fraud detection algorithms analyzing patterns across claims data"
        ],
        benefits: "Advanced analytics can enable 70-75% improvement in risk assessment precision, 45-50% faster claims resolution, and creation of personalized insurance products."
      }
    ]
  },
  {
    industry: "EnergyTech",
    color: "from-green-500 via-emerald-500 to-teal-500",
    cases: [
      {
        title: "Smart Grid Optimization",
        slug: "smart-grid-optimization",
        challenge: "Energy providers face increasing demands for reliability, sustainability, and cost efficiency while integrating renewable sources and managing peak loads.",
        applications: [
          "AI-powered demand forecasting with weather and consumption pattern analysis",
          "Real-time grid management with automated load balancing",
          "Predictive maintenance for critical infrastructure components",
          "Renewable energy integration optimization algorithms"
        ],
        benefits: "Implementation results in 30-35% improved grid reliability, 25% reduction in energy waste, and 40% better renewable energy utilization."
      }
    ]
  },
  {
    industry: "HealthTech",
    color: "from-cyan-500 via-blue-600 to-purple-600",
    cases: [
      {
        title: "Medical Imaging Analysis",
        slug: "medical-imaging-analysis",
        challenge: "Healthcare providers require faster, more accurate diagnostic capabilities for medical imagery while reducing radiologist workload and improving early detection rates.",
        applications: [
          "Deep learning-based anomaly detection for X-rays, MRIs, and CT scans",
          "Automated prioritization of urgent cases in radiology workflows",
          "Comparative analysis with historical patient data",
          "3D reconstruction and visualization for surgical planning"
        ],
        benefits: "System deployment shows 92% accuracy in anomaly detection, 60% reduction in analysis time, and 45% improvement in early diagnosis rates."
      }
    ]
  }
];

// 1. Flatten all use cases into case study cards
const useCaseCards = industryUseCases.flatMap((industry) =>
  industry.cases.map((useCase, idx) => ({
    id: `usecase-${industry.industry}-${idx}`,
    title: useCase.title,
    client: industry.industry + " Use Case",
    description: useCase.challenge,
    image: undefined, // No image for use cases
    tags: [industry.industry, ...useCase.applications.slice(0, 2)], // Industry + 2 key apps
    slug: useCase.slug || `industry/${industry.industry.toLowerCase()}`, // Prioritize explicit slug
    gradient: industry.color
  }))
);

// 2. Merge with caseStudies
const allPortfolioCards = [...caseStudies, ...useCaseCards];

// Filter out any compliance-related use case cards from allPortfolioCards
// const filteredPortfolioCards = allPortfolioCards.filter(
//   study => study.slug !== 'compliance-scanner' && !(study.slug && study.slug.toLowerCase().includes('compliance'))
// );

// Helper to determine text color based on gradient
function getTextColor(gradient: string) {
  if (
    gradient.includes('from-red-500') ||
    gradient.includes('from-orange-400') ||
    gradient.includes('from-amber-500') ||
    gradient.includes('from-yellow-500') ||
    gradient.includes('from-orange-500') ||
    gradient.includes('from-amber-400')
  ) {
    return 'text-gray-900'; // dark text for orange/yellow gradients
  }
  return 'text-white'; // white text for blue/purple gradients
}

function getTagBg(gradient: string) {
  if (
    gradient.includes('from-red-500') ||
    gradient.includes('from-orange-400') ||
    gradient.includes('from-amber-500') ||
    gradient.includes('from-yellow-500') ||
    gradient.includes('from-orange-500') ||
    gradient.includes('from-amber-400')
  ) {
    return 'bg-white/80 text-orange-700';
  }
  return 'bg-white/20 text-white';
}

export default function Portfolio() {
  // Since this is a client component, we use useParams hook 
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  // Apply localization to all portfolio cards, including both case studies and use cases
  const localizedPortfolioCards = allPortfolioCards.map(study => ({
    ...study,
    title: localizeSpelling(study.title, currentLocale),
    client: localizeSpelling(study.client, currentLocale),
    description: localizeSpelling(study.description, currentLocale),
    tags: study.tags.map(tag => 
      localizeSpelling(tag, currentLocale)
    )
  }));

  return (
    <div className="container mx-auto max-w-7xl px-4 py-16 mt-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-12"
      >
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold tracking-tight">{localizeSpelling("Case Studies", currentLocale)}</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {localizeSpelling("Explore how we've helped businesses transform with AI technology", currentLocale)}
          </p>
        </div>

        <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {localizedPortfolioCards.map((study, index) => {
            const textColor = getTextColor(study.gradient);
            const tagBg = getTagBg(study.gradient);
            const linkHref = `/${currentLocale}/portfolio/${study.slug.replace('industry/', '')}`;
            return (
            <motion.div
              key={study.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02, transition: { duration: 0.3 } }}
            >
                <Link href={linkHref}>
                <div className={`rounded-xl overflow-hidden h-full relative group shadow-lg hover:shadow-2xl transition-all duration-300`}>
                  {/* Animated gradient background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${study.gradient} opacity-90 group-hover:opacity-100 transition-opacity duration-700`}></div>
                  {/* Shimmer effect */}
                  <div className="absolute inset-0 bg-[length:200%_200%] animate-shimmer opacity-30"
                       style={{
                         backgroundImage: 'linear-gradient(115deg, transparent 25%, rgba(255, 255, 255, 0.4) 50%, transparent 75%)',
                         backgroundSize: '200% 200%',
                         animation: 'shimmer 12s infinite linear'
                       }}>
                  </div>
                  {/* Card content */}
                    <Card className={`h-full border-0 bg-transparent backdrop-blur-sm relative z-10 ${textColor}`}>
                      {study.title === "AI-Powered Customer Service" && (
                        <div className="relative aspect-video bg-white rounded-t-lg flex flex-col shadow-inner overflow-hidden">
                          {/* Header Bar */}
                          <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-violet-600 to-indigo-500 text-white text-sm font-semibold">
                            <span>AI Chatbot</span>
                            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Online</span>
                          </div>
                          {/* Main Content */}
                          <div className="flex-1 flex flex-col md:flex-row">
                            {/* Chat Window */}
                            <div className="flex-1 px-4 py-3 space-y-2 overflow-y-auto text-sm bg-white">
                              <div className="flex justify-start">
                                <div className="bg-gray-100 text-gray-800 rounded-lg px-3 py-2 max-w-[70%]">Hi! How can I help you today?</div>
                              </div>
                              <div className="flex justify-end">
                                <div className="bg-violet-100 text-violet-900 rounded-lg px-3 py-2 max-w-[70%]">I need to check my order status.</div>
                              </div>
                              <div className="flex justify-start">
                                <div className="bg-gray-100 text-gray-800 rounded-lg px-3 py-2 max-w-[70%]">Sure! Please provide your order number.</div>
                              </div>
                              <div className="flex justify-end">
                                <div className="bg-violet-100 text-violet-900 rounded-lg px-3 py-2 max-w-[70%]">#123456</div>
                              </div>
                              <div className="flex justify-start">
                                <div className="bg-gray-100 text-gray-800 rounded-lg px-3 py-2 max-w-[70%]">Thank you! Your order is on the way and will arrive tomorrow.</div>
                              </div>
                            </div>
                            {/* Sidebar Stats */}
                            <div className="w-40 bg-gray-50 border-l border-gray-200 flex flex-col justify-center px-3 py-2 text-xs text-gray-700">
                              <div className="mb-2 flex items-center justify-between">
                                <span>Response Time</span>
                                <span className="font-bold text-violet-600">-80%</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Satisfaction</span>
                                <span className="font-bold text-green-600">95%</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Support Tickets</span>
                                <span className="font-bold text-blue-600">-60%</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Active Users</span>
                                <span className="font-bold text-indigo-600">1,250</span>
                              </div>
                            </div>
                          </div>
                          {/* Input Bar (disabled look) */}
                          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center gap-2">
                            <input className="flex-1 bg-gray-100 rounded-full px-3 py-1 text-xs text-gray-500" placeholder="Type a message..." disabled />
                            <button className="bg-violet-500 text-white rounded-full px-3 py-1 text-xs opacity-60 cursor-not-allowed" disabled>Send</button>
                          </div>
                          {/* Status Bar */}
                          <div className="px-4 py-1 bg-gray-100 border-t border-gray-200 text-xs text-gray-600 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                            <span>AI Assistant Online</span>
                          </div>
                          {/* Tech Stack Summary */}
                          <div className="px-4 py-1 bg-gradient-to-r from-violet-50 to-indigo-100 border-t border-violet-200 text-xs text-indigo-800 flex items-center gap-2 justify-between">
                            <span>Tech: NLP, Machine Learning, Intent Recognition</span>
                            <span className="font-semibold">Powered by OpenLabs AI</span>
                          </div>
                        </div>
                      )}
                      {study.title === "Computer Vision for Quality Control" && (
                        <div className="relative aspect-video bg-white rounded-t-lg flex flex-col shadow-inner overflow-hidden">
                          {/* Header Bar */}
                          <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 text-white text-sm font-semibold">
                            <span>Quality Control Dashboard</span>
                            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Live</span>
                          </div>
                          {/* Main Content */}
                          <div className="flex-1 flex flex-col md:flex-row">
                            {/* Simulated Product Frame */}
                            <div className="flex-1 flex items-center justify-center relative bg-gray-50">
                              <div className="w-44 h-32 bg-gray-200 rounded-lg relative overflow-hidden">
                                {/* Product image placeholder */}
                                <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-300 rounded-lg" />
                                {/* Defect bounding boxes */}
                                <div className="absolute border-2 border-red-500 rounded-sm left-4 top-6 w-12 h-8 animate-pulse">
                                  <span className="absolute -top-5 left-0 text-xs bg-red-500 text-white px-1 rounded">Defect</span>
                                </div>
                                <div className="absolute border-2 border-yellow-400 rounded-sm right-3 bottom-5 w-8 h-6 animate-pulse">
                                  <span className="absolute -bottom-5 left-0 text-xs bg-yellow-400 text-white px-1 rounded">Warning</span>
                                </div>
                              </div>
                            </div>
                            {/* Sidebar Stats */}
                            <div className="w-40 bg-gray-50 border-l border-gray-200 flex flex-col justify-center px-3 py-2 text-xs text-gray-700">
                              <div className="mb-2 flex items-center justify-between">
                                <span>Total Inspected</span>
                                <span className="font-bold text-gray-900">1,250</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Defects Found</span>
                                <span className="font-bold text-red-500">3</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Warnings</span>
                                <span className="font-bold text-yellow-500">2</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Accuracy</span>
                                <span className="font-bold text-green-600">99.7%</span>
                              </div>
                            </div>
                          </div>
                          {/* Status Bar */}
                          <div className="px-4 py-1 bg-gray-100 border-t border-gray-200 text-xs text-gray-600 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                            <span>System Operational</span>
                          </div>
                          {/* Tech Stack Summary */}
                          <div className="px-4 py-1 bg-gradient-to-r from-cyan-50 to-blue-100 border-t border-cyan-200 text-xs text-blue-800 flex items-center gap-2 justify-between">
                            <span>Tech: Computer Vision, Image Processing, Edge Computing</span>
                            <span className="font-semibold">Powered by OpenLabs AI</span>
                          </div>
                        </div>
                      )}
                      {study.title === "Predictive Analytics Platform" && (
                        <div className="relative aspect-video bg-white rounded-t-lg flex flex-col shadow-inner overflow-hidden">
                          {/* Header Bar */}
                          <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white text-sm font-semibold">
                            <span>Predictive Analytics Dashboard</span>
                            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Live</span>
                          </div>
                          {/* Main Content */}
                          <div className="flex-1 flex flex-col md:flex-row">
                            {/* Chart Area */}
                            <div className="flex-1 flex items-center justify-center bg-gray-50">
                              <svg viewBox="0 0 180 80" className="w-full h-28 md:h-32">
                                {/* Axes */}
                                <line x1="30" y1="10" x2="30" y2="70" stroke="#d1d5db" strokeWidth="2" />
                                <line x1="30" y1="70" x2="170" y2="70" stroke="#d1d5db" strokeWidth="2" />
                                {/* Actual values (dotted) */}
                                <polyline points="30,60 60,50 90,55 120,40 150,30 170,35" fill="none" stroke="#6366f1" strokeWidth="2" strokeDasharray="4 2" />
                                {/* Predicted values (solid) */}
                                <polyline points="30,62 60,52 90,54 120,38 150,28 170,32" fill="none" stroke="#f472b6" strokeWidth="3" />
                                {/* Dots */}
                                <circle cx="30" cy="60" r="2" fill="#6366f1" />
                                <circle cx="60" cy="50" r="2" fill="#6366f1" />
                                <circle cx="90" cy="55" r="2" fill="#6366f1" />
                                <circle cx="120" cy="40" r="2" fill="#6366f1" />
                                <circle cx="150" cy="30" r="2" fill="#6366f1" />
                                <circle cx="170" cy="35" r="2" fill="#6366f1" />
                                {/* Legend */}
                                <rect x="40" y="15" width="10" height="3" fill="#f472b6" />
                                <text x="55" y="20" fontSize="8" fill="#6b7280">Predicted</text>
                                <rect x="100" y="15" width="10" height="3" fill="#6366f1" />
                                <text x="115" y="20" fontSize="8" fill="#6b7280">Actual</text>
                              </svg>
                            </div>
                            {/* Sidebar Stats */}
                            <div className="w-40 bg-gray-50 border-l border-gray-200 flex flex-col justify-center px-3 py-2 text-xs text-gray-700">
                              <div className="mb-2 flex items-center justify-between">
                                <span>Downtime Reduced</span>
                                <span className="font-bold text-green-600">60%</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Alerts</span>
                                <span className="font-bold text-fuchsia-500">2</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Prediction Accuracy</span>
                                <span className="font-bold text-indigo-600">97.2%</span>
                              </div>
                            </div>
                          </div>
                          {/* Status Bar */}
                          <div className="px-4 py-1 bg-gray-100 border-t border-gray-200 text-xs text-gray-600 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                            <span>All Systems Normal</span>
                          </div>
                          {/* Tech Stack Summary */}
                          <div className="px-4 py-1 bg-gradient-to-r from-fuchsia-50 to-indigo-100 border-t border-fuchsia-200 text-xs text-fuchsia-800 flex items-center gap-2 justify-between">
                            <span>Tech: Machine Learning, Analytics, IoT</span>
                            <span className="font-semibold">Powered by OpenLabs AI</span>
                          </div>
                        </div>
                      )}
                      {study.title === "Compliance Scanner" && (
                        <div className="relative aspect-video bg-white rounded-t-lg flex flex-col shadow-inner overflow-hidden">
                          {/* Header Bar */}
                          <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-red-500 to-amber-500 text-white text-sm font-semibold">
                            <span>Compliance Scanner</span>
                            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Scan Complete</span>
                          </div>
                          {/* Main Content */}
                          <div className="flex-1 flex flex-col md:flex-row">
                            {/* Vehicle/Part Frame */}
                            <div className="flex-1 flex items-center justify-center bg-gray-50 relative">
                              <div className="w-32 h-20 bg-gray-200 rounded-lg relative overflow-hidden mt-4">
                                {/* Vehicle/part image placeholder */}
                                <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-300 rounded-lg" />
                                {/* Compliance highlights */}
                                <div className="absolute left-4 top-3 flex items-center gap-1">
                                  <span className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center text-white text-xs">✓</span>
                                  <span className="text-xs text-green-700 font-bold">Brakes</span>
                                </div>
                                <div className="absolute right-4 top-8 flex items-center gap-1">
                                  <span className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">✗</span>
                                  <span className="text-xs text-red-700 font-bold">Emissions</span>
                                </div>
                                <div className="absolute left-8 bottom-3 flex items-center gap-1">
                                  <span className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center text-white text-xs">✓</span>
                                  <span className="text-xs text-green-700 font-bold">Lights</span>
                                </div>
                              </div>
                            </div>
                            {/* Sidebar Compliance Stats */}
                            <div className="w-40 bg-gray-50 border-l border-gray-200 flex flex-col justify-center px-3 py-2 text-xs text-gray-700">
                              <div className="mb-2 flex items-center justify-between">
                                <span>Regulations Checked</span>
                                <span className="font-bold text-gray-900">12</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Violations</span>
                                <span className="font-bold text-red-500">1</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Scan Accuracy</span>
                                <span className="font-bold text-green-600">93%</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Manual Review</span>
                                <span className="font-bold text-amber-500">Required</span>
                              </div>
                            </div>
                          </div>
                          {/* Status Bar */}
                          <div className="px-4 py-1 bg-gray-100 border-t border-gray-200 text-xs text-gray-600 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                            <span>Compliant (except Emissions)</span>
                          </div>
                          {/* Tech Stack Summary */}
                          <div className="px-4 py-1 bg-gradient-to-r from-orange-50 to-amber-100 border-t border-orange-200 text-xs text-orange-800 flex items-center gap-2 justify-between">
                            <span>Tech: Computer Vision, Sensor Fusion, Compliance Automation</span>
                            <span className="font-semibold">Powered by OpenLabs AI</span>
                          </div>
                        </div>
                      )}
                      {study.title === "Fleet Intelligence & Management Systems" && (
                        <div className="relative aspect-video bg-white rounded-t-lg flex flex-col shadow-inner overflow-hidden">
                          {/* Header Bar */}
                          <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-red-500 to-amber-500 text-white text-sm font-semibold">
                            <span>Fleet Management Dashboard</span>
                            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Live</span>
                          </div>
                          {/* Main Content */}
                          <div className="flex-1 flex flex-col md:flex-row">
                            {/* Map Area */}
                            <div className="flex-1 flex items-center justify-center bg-gray-50 relative">
                              <div className="w-40 h-24 bg-blue-100 rounded-lg relative overflow-hidden mt-4 flex items-center justify-center">
                                {/* Map grid */}
                                <div className="absolute inset-0 opacity-60">
                                  <svg viewBox="0 0 160 80" className="w-full h-full">
                                    <rect x="0" y="0" width="160" height="80" fill="#e0e7ef" />
                                    <line x1="40" y1="0" x2="40" y2="80" stroke="#c7d2fe" strokeWidth="1" />
                                    <line x1="80" y1="0" x2="80" y2="80" stroke="#c7d2fe" strokeWidth="1" />
                                    <line x1="120" y1="0" x2="120" y2="80" stroke="#c7d2fe" strokeWidth="1" />
                                    <line x1="0" y1="20" x2="160" y2="20" stroke="#c7d2fe" strokeWidth="1" />
                                    <line x1="0" y1="40" x2="160" y2="40" stroke="#c7d2fe" strokeWidth="1" />
                                    <line x1="0" y1="60" x2="160" y2="60" stroke="#c7d2fe" strokeWidth="1" />
                                  </svg>
                                </div>
                                {/* Vehicle icons */}
                                <div className="absolute left-8 top-6 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow">A</div>
                                <div className="absolute right-10 top-12 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow">M</div>
                                <div className="absolute left-20 bottom-6 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow">T</div>
                                <div className="absolute right-6 bottom-8 w-5 h-5 bg-gray-400 rounded-full flex items-center justify-center text-white text-xs font-bold shadow">O</div>
                                {/* Legend */}
                                <div className="absolute left-2 bottom-2 flex gap-2 text-xs">
                                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-full inline-block"></span>Active</span>
                                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-500 rounded-full inline-block"></span>Maint.</span>
                                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-500 rounded-full inline-block"></span>Transit</span>
                                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-gray-400 rounded-full inline-block"></span>Offline</span>
                                </div>
                              </div>
                            </div>
                            {/* Sidebar Fleet Stats */}
                            <div className="w-40 bg-gray-50 border-l border-gray-200 flex flex-col justify-center px-3 py-2 text-xs text-gray-700">
                              <div className="mb-2 flex items-center justify-between">
                                <span>Vehicles Active</span>
                                <span className="font-bold text-green-600">42</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Maintenance</span>
                                <span className="font-bold text-amber-500">7</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>In Transit</span>
                                <span className="font-bold text-blue-500">35</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Offline</span>
                                <span className="font-bold text-gray-500">2</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Efficiency</span>
                                <span className="font-bold text-green-700">94%</span>
                              </div>
                            </div>
                          </div>
                          {/* Status Bar */}
                          <div className="px-4 py-1 bg-gray-100 border-t border-gray-200 text-xs text-gray-600 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                            <span>Fleet Operational</span>
                          </div>
                          {/* Tech Stack Summary */}
                          <div className="px-4 py-1 bg-gradient-to-r from-orange-50 to-amber-100 border-t border-orange-200 text-xs text-orange-800 flex items-center gap-2 justify-between">
                            <span>Tech: IoT, Predictive Analytics, Real-time Monitoring</span>
                            <span className="font-semibold">Powered by OpenLabs AI</span>
                          </div>
                        </div>
                      )}
                      {study.title === "Risk Assessment & Specialized Coverage Models" && (
                        <div className="relative aspect-video bg-white rounded-t-lg flex flex-col shadow-inner overflow-hidden">
                          {/* Header Bar */}
                          <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-red-500 to-amber-500 text-white text-sm font-semibold">
                            <span>Insurance Risk Dashboard</span>
                            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Live</span>
                          </div>
                          {/* Main Content */}
                          <div className="flex-1 flex flex-col md:flex-row">
                            {/* Risk Score Gauge & Claims */}
                            <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 relative py-2">
                              {/* Risk Score Gauge */}
                              <div className="w-24 h-24 relative flex items-center justify-center mb-2">
                                <svg viewBox="0 0 100 50" className="w-full h-full">
                                  <path d="M10,50 A40,40 0 0,1 90,50" fill="none" stroke="#fbbf24" strokeWidth="8" />
                                  <path d="M10,50 A40,40 0 0,1 70,18" fill="none" stroke="#22c55e" strokeWidth="8" />
                                  <circle cx="50" cy="50" r="6" fill="#fff" stroke="#fbbf24" strokeWidth="2" />
                                  <line x1="50" y1="50" x2="70" y2="18" stroke="#ef4444" strokeWidth="3" />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                  <span className="text-lg font-bold text-amber-600">Risk: 72</span>
                                  <span className="text-xs text-gray-500">Medium</span>
                                </div>
                              </div>
                              {/* Claims Summary */}
                              <div className="w-full flex flex-col items-center mt-2">
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">Claims Processed: 1,204</span>
                                  <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">Fraud Alerts: 3</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs mt-1">
                                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">Dynamic Premiums</span>
                                  <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">Usage-Based</span>
                                </div>
                              </div>
                            </div>
                            {/* Sidebar Risk Metrics */}
                            <div className="w-40 bg-gray-50 border-l border-gray-200 flex flex-col justify-center px-3 py-2 text-xs text-gray-700">
                              <div className="mb-2 flex items-center justify-between">
                                <span>Risk Score</span>
                                <span className="font-bold text-amber-600">72</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Claims Processed</span>
                                <span className="font-bold text-green-600">1,204</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Fraud Alerts</span>
                                <span className="font-bold text-red-500">3</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Dynamic Premiums</span>
                                <span className="font-bold text-blue-600">Yes</span>
                              </div>
                            </div>
                          </div>
                          {/* Status Bar */}
                          <div className="px-4 py-1 bg-gray-100 border-t border-gray-200 text-xs text-gray-600 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                            <span>Risk Monitoring Active</span>
                          </div>
                          {/* Tech Stack Summary */}
                          <div className="px-4 py-1 bg-gradient-to-r from-orange-50 to-amber-100 border-t border-orange-200 text-xs text-orange-800 flex items-center gap-2 justify-between">
                            <span>Tech: AI Risk Models, Computer Vision, Fraud Detection</span>
                            <span className="font-semibold">Powered by OpenLabs AI</span>
                          </div>
                        </div>
                      )}
                      {study.title === "Smart Grid Optimization" && (
                        <div className="relative aspect-video bg-white rounded-t-lg flex flex-col shadow-inner overflow-hidden">
                          {/* Header Bar */}
                          <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-green-500 to-teal-500 text-white text-sm font-semibold">
                            <span>Energy Grid Dashboard</span>
                            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Live</span>
                          </div>
                          {/* Main Content */}
                          <div className="flex-1 flex flex-col md:flex-row">
                            {/* Grid Visualization */}
                            <div className="flex-1 flex items-center justify-center bg-gray-50 relative">
                              <div className="w-40 h-24 bg-emerald-50 rounded-lg relative overflow-hidden mt-4 flex items-center justify-center">
                                {/* Grid visualization */}
                                <svg viewBox="0 0 160 80" className="w-full h-full">
                                  <rect x="0" y="0" width="160" height="80" fill="#ecfdf5" />
                                  <line x1="0" y1="20" x2="160" y2="20" stroke="#10b981" strokeWidth="1" />
                                  <line x1="0" y1="40" x2="160" y2="40" stroke="#10b981" strokeWidth="1" />
                                  <line x1="0" y1="60" x2="160" y2="60" stroke="#10b981" strokeWidth="1" />
                                  <line x1="40" y1="0" x2="40" y2="80" stroke="#10b981" strokeWidth="1" />
                                  <line x1="80" y1="0" x2="80" y2="80" stroke="#10b981" strokeWidth="1" />
                                  <line x1="120" y1="0" x2="120" y2="80" stroke="#10b981" strokeWidth="1" />
                                  
                                  {/* Energy flow visualization */}
                                  <circle cx="20" cy="30" r="6" fill="#059669" opacity="0.7" />
                                  <circle cx="60" cy="50" r="8" fill="#059669" opacity="0.7" />
                                  <circle cx="100" cy="25" r="10" fill="#059669" opacity="0.7" />
                                  <circle cx="140" cy="60" r="7" fill="#059669" opacity="0.7" />
                                  
                                  {/* Connecting lines */}
                                  <line x1="20" y1="30" x2="60" y2="50" stroke="#059669" strokeWidth="2" />
                                  <line x1="60" y1="50" x2="100" y2="25" stroke="#059669" strokeWidth="2" />
                                  <line x1="100" y1="25" x2="140" y2="60" stroke="#059669" strokeWidth="2" />
                                  
                                  {/* Solar icons */}
                                  <rect x="15" y="10" width="10" height="10" fill="#fbbf24" rx="2" />
                                  <rect x="95" y="5" width="10" height="10" fill="#fbbf24" rx="2" />
                                  
                                  {/* Wind icons */}
                                  <circle cx="60" cy="15" r="5" fill="#60a5fa" />
                                  <circle cx="135" cy="20" r="5" fill="#60a5fa" />
                                </svg>
                                
                                {/* Legend */}
                                <div className="absolute left-2 bottom-2 flex gap-2 text-xs">
                                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-full inline-block"></span>Grid</span>
                                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-400 rounded-full inline-block"></span>Solar</span>
                                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-400 rounded-full inline-block"></span>Wind</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Sidebar Energy Stats */}
                            <div className="w-40 bg-gray-50 border-l border-gray-200 flex flex-col justify-center px-3 py-2 text-xs text-gray-700">
                              <div className="mb-2 flex items-center justify-between">
                                <span>Grid Reliability</span>
                                <span className="font-bold text-emerald-600">95%</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Energy Waste</span>
                                <span className="font-bold text-red-500">-25%</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Renewable Usage</span>
                                <span className="font-bold text-blue-600">40%</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Peak Load</span>
                                <span className="font-bold text-amber-600">78%</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Forecasting</span>
                                <span className="font-bold text-emerald-700">Active</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Status Bar */}
                          <div className="px-4 py-1 bg-gray-100 border-t border-gray-200 text-xs text-gray-600 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                            <span>Grid Balanced | Demand Normal</span>
                          </div>
                          
                          {/* Tech Stack Summary */}
                          <div className="px-4 py-1 bg-gradient-to-r from-green-50 to-emerald-100 border-t border-green-200 text-xs text-emerald-800 flex items-center gap-2 justify-between">
                            <span>Tech: AI Forecasting, Real-time Management, IoT</span>
                            <span className="font-semibold">Powered by OpenLabs AI</span>
                          </div>
                        </div>
                      )}
                      {study.title === "Medical Imaging Analysis" && (
                        <div className="relative aspect-video bg-white rounded-t-lg flex flex-col shadow-inner overflow-hidden">
                          {/* Header Bar */}
                          <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 text-white text-sm font-semibold">
                            <span>Medical Imaging Dashboard</span>
                            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Live</span>
                          </div>
                          
                          {/* Main Content */}
                          <div className="flex-1 flex flex-col md:flex-row">
                            {/* Imaging Visualization */}
                            <div className="flex-1 flex items-center justify-center bg-gray-50 relative">
                              <div className="w-40 h-24 bg-indigo-50 rounded-lg relative overflow-hidden mt-4 flex items-center justify-center">
                                {/* Simulated medical scan */}
                                <div className="absolute inset-0 bg-black/10"></div>
                                
                                {/* Brain scan visualization */}
                                <svg viewBox="0 0 160 80" className="w-full h-full">
                                  <ellipse cx="80" cy="40" rx="55" ry="35" fill="#e0e7ff" stroke="#6366f1" strokeWidth="1" />
                                  <path d="M50,40 C60,20 100,20 110,40 C100,60 60,60 50,40 Z" fill="#c7d2fe" stroke="#6366f1" strokeWidth="1" />
                                  <path d="M65,30 C75,25 85,25 95,30" fill="none" stroke="#6366f1" strokeWidth="1" />
                                  <path d="M65,50 C75,55 85,55 95,50" fill="none" stroke="#6366f1" strokeWidth="1" />
                                  
                                  {/* Anomaly highlight */}
                                  <circle cx="100" cy="30" r="6" fill="none" stroke="#ef4444" strokeWidth="2" className="animate-pulse" />
                                  <circle cx="70" cy="35" r="4" fill="none" stroke="#fbbf24" strokeWidth="2" className="animate-pulse" />
                                </svg>
                                
                                {/* Markers */}
                                <div className="absolute right-3 top-3 text-xs bg-red-500 text-white px-1 rounded font-bold">Anomaly</div>
                                <div className="absolute left-3 bottom-3 text-xs bg-indigo-500 text-white px-1 rounded font-bold">MRI-32</div>
                              </div>
                            </div>
                            
                            {/* Sidebar Analysis Stats */}
                            <div className="w-40 bg-gray-50 border-l border-gray-200 flex flex-col justify-center px-3 py-2 text-xs text-gray-700">
                              <div className="mb-2 flex items-center justify-between">
                                <span>Accuracy</span>
                                <span className="font-bold text-indigo-600">92%</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Analysis Time</span>
                                <span className="font-bold text-green-600">-60%</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Anomalies</span>
                                <span className="font-bold text-red-500">2</span>
                              </div>
                              <div className="mb-2 flex items-center justify-between">
                                <span>Confidence</span>
                                <span className="font-bold text-indigo-600">High</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Early Detection</span>
                                <span className="font-bold text-green-700">+45%</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Status Bar */}
                          <div className="px-4 py-1 bg-gray-100 border-t border-gray-200 text-xs text-gray-600 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                            <span>Analysis Complete | Radiologist Review</span>
                          </div>
                          
                          {/* Tech Stack Summary */}
                          <div className="px-4 py-1 bg-gradient-to-r from-indigo-50 to-purple-100 border-t border-indigo-200 text-xs text-indigo-800 flex items-center gap-2 justify-between">
                            <span>Tech: Deep Learning, Computer Vision, Diagnostic AI</span>
                            <span className="font-semibold">Powered by OpenLabs AI</span>
                          </div>
                        </div>
                      )}
                      {/* Default card content for use cases and case studies */}
                      {(!["AI-Powered Customer Service",
                          "Computer Vision for Quality Control",
                          "Predictive Analytics Platform", 
                          "Compliance Scanner",
                          "Fleet Intelligence & Management Systems",
                          "Risk Assessment & Specialized Coverage Models",
                          "Smart Grid Optimization",
                          "Medical Imaging Analysis"].includes(study.title)) && (
                        <>
                    <CardHeader>
                            <CardTitle className={`flex items-center justify-between ${textColor}`}>
                        {study.title}
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ml-2 ${tagBg}`}>{study.tags[0]}</span>
                      </CardTitle>
                            <p className={`text-sm ${textColor} opacity-80`}>{study.client}</p>
                    </CardHeader>
                    <CardContent>
                            <p className={`${textColor} opacity-90 mb-4`}>{study.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {study.tags.map(tag => (
                          <span
                            key={tag}
                                  className={`px-2 py-1 rounded-full text-sm font-medium ${tagBg}`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </CardContent>
                        </>
                      )}
                  </Card>
                </div>
              </Link>
            </motion.div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}